/**
 * This file includes utility functions that can extract user data from the idToken generated after authentication.
 */
import { Claims, AvpContextClaims } from '@org-avp/context-claims';
import { getStorageItem } from '../ui/storage';
import { getCountryISO2 } from '../isoConverter';
import { memoize } from 'utils/functions';
import { IUserInfo, User } from './user';
import { IMarket } from 'data/schema';

const getIdTokenPayload = memoize((idToken: string): Claims | null => {
  try {
    if (!idToken) return null;
    const tokenBody = atob(idToken.split('.')[1]);
    return JSON.parse(tokenBody);
  } catch (err) {
    console.error(`Failed to retrieve token:\n${err}`);
    return null;
  }
});

const extractAvpUserData = memoize((claims: Claims): IUserInfo => {
  const hasClaims = claims && Object.keys(claims).length;
  const userType = hasClaims ? 'dealer' : 'customer';
  const allBrands = claims ? AvpContextClaims.getBrands(claims) : [];
  const brand = allBrands[0] || (claims ? JSON.parse(claims['custom:brands'] ?? '[]')[0] : undefined) || 'A';
  const market =
    (claims && AvpContextClaims.getMarkets(claims)[0]) || JSON.parse(claims?.['custom:markets'] ?? '[]')?.[0];
  const iso2 = getCountryISO2(market) || 'de';
  const iso3 = (market ?? 'DEU') as IMarket;
  const groups = claims ? AvpContextClaims.getGroups(claims) : [];
  const email = `${claims?.email ?? ''}`;
  const name = claims ? `${claims['custom:firstname'] ?? ''} ${claims['custom:lastname'] ?? ''}`.trim() : '';
  const phoneNumber = `${claims?.['custom:phone'] ?? ''}`;
  const publicPicture = `${claims?.['custom:publicPicture'] ?? ''}`;
  const dealerIdKvps = claims ? (AvpContextClaims.getDealerIds(claims)?.[0] ?? '') : '';
  const userPseudonym = claims ? (AvpContextClaims.getUserPseudonym(claims) ?? '') : '';
  const dealerIdsList: string[] = AvpContextClaims.getDealerIds(claims);
  const markets: string[] = claims
    ? AvpContextClaims.getMarkets(claims).length
      ? AvpContextClaims.getMarkets(claims)
      : JSON.parse(claims['custom:markets'] ?? '[]')
    : [];

  return {
    type: userType,
    brand,
    brands: allBrands,
    market: { iso2, iso3 },
    groups,
    email,
    name,
    phoneNumber,
    publicPicture,
    dealerIdKvps,
    userPseudonym,
    dealerIdsList,
    markets,
  };
});

export function initAvpAuthentication() {
  User.config.getUserInfoCallback = (idToken) => {
    const claims = idToken ? getIdTokenPayload(idToken) : null;
    if (!claims) {
      // No claims means user is not logged in
      return null;
    }
    return extractAvpUserData(claims);
  };

  User.config.getAccessTokenCallback = () => {
    return getStorageItem('sessionStorage', 'auth:token')?.accessToken ?? '';
  };

  User.config.getIdTokenCallback = () => {
    return getStorageItem('sessionStorage', 'auth:token')?.idToken ?? '';
  };
}
